import DB from '@/core/services/db';
import { useTenant } from '@/core';
import { useAuth } from '@/core';
import { TenantConfig } from '@/types/interface/TenantConfig.interface';
import { computed, reactive } from 'vue';

export const DBExercises = 'documentationToolExercises' as const;

const setup = () => {
  const database = reactive<DB>({});

  const { tenant } = useTenant<TenantConfig>();
  const { user } = useAuth();

  const namespace = computed(() => {
    const tenantNamespace = tenant.value.customElementOptions?.parentNamespace || tenant.value.namespace;
    if (!tenantNamespace) {
      console.error('Missing `namespaceId`');
    }
    return tenantNamespace;
  });

  const generateId = (id: string, productId: string, userId: string) => `${id}:${productId}:${userId}`;

  const getDbName = () => {
    return tenant.value?.isCustomElement
      ? generateId(DBExercises, tenant.value.customElementOptions.parentNamespace, user.value?._id)
      : DBExercises;
  };

  const initDatabase = () => {
    const dbName = getDbName();
    // NOTE: This is done so users won't have shared their projects from previous database, need to watch out for prefix changes
    indexedDB.deleteDatabase(`_pouch_${dbName}`);

    Object.assign(
      database,
      new DB(getDbName(), {
        auto_compaction: true,
      }),
    );
  };

  const getSelector = () => ({
    entryType: DBExercises,
    productId: namespace.value,
  });

  const replicationOptions = () => ({
    selector: getSelector(),
    onCompleted: (info) => {
      console.debug('synchronization from remote completed', info);
    },
    onError: (err) => {
      console.error('synchronization from remote error', err);
    },
  });

  // Replication to remote must include deleted entries (even those without fields specified in selector)
  const replicationToOptions = () => ({
    selector: {
      $or: [
        getSelector(),
        {
          _deleted: true,
        },
      ],
    },
    onCompleted: (info) => {
      console.debug('synchronization to remote completed', info);
    },
    onError: (err) => {
      console.error('synchronization to remote error', err);
    },
  });

  return {
    database,
    initDatabase,
    replicationOptions,
    replicationToOptions,
  };
};

let instance: ReturnType<typeof setup> = undefined;

export default () => {
  if (instance === undefined) {
    instance = setup();
  }

  return instance;
};
